import { lazy, type ReactNode, Suspense } from 'react';

import { config } from '@/config/config';
import { WorkspacePlanContext } from '@/modules/billing/hooks/useWorkspacePlan';

const StiggProvider = lazy(() =>
  import('@/lib/stigg/StiggProvider').then((module) => ({
    default: module.StiggProvider,
  })),
);

const BillingPlanProvider = lazy(() =>
  import('./BillingPlanProvider').then((module) => ({
    default: module.BillingPlanProvider,
  })),
);

interface PortalBillingProviderProps {
  tenantId?: string;
  children: ReactNode;
}

/**
 * Review pages also render before the lazy Stigg provider mounts, so they cannot
 * call its hooks directly. The SDK-free Context supplies loading state until
 * BillingPlanProvider can derive the real plan. No subscription state is copied.
 */
export const PortalBillingProvider = ({
  tenantId,
  children,
}: PortalBillingProviderProps) => {
  const localPlanOverride =
    config.app.env === 'localhost'
      ? config.billing.workspacePlanOverride
      : undefined;

  if (localPlanOverride) {
    return (
      <WorkspacePlanContext
        value={{ plan: localPlanOverride, isLoading: false }}
      >
        {children}
      </WorkspacePlanContext>
    );
  }

  if (!tenantId) return children;

  return (
    <WorkspacePlanContext value={{ plan: undefined, isLoading: true }}>
      <Suspense fallback={children}>
        <StiggProvider key={tenantId} customerId={tenantId}>
          <BillingPlanProvider>{children}</BillingPlanProvider>
        </StiggProvider>
      </Suspense>
    </WorkspacePlanContext>
  );
};
